import {
  CloseOutlined,
  EyeOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { Button, Col, Form, Input, Popconfirm, Row, Space, Switch, Tooltip, Typography } from 'antd';
import React, { useRef } from 'react';
import {
  NUMBER_FIELD_TYPES,
  TOY_CONTENT_LIST_LIMIT,
} from '../../../../../common/constants';
import { formValidatorRules } from '../../../../../common/utils';
import NumberComponent from '../../../../../components/NumberComponent';

const { required } = formValidatorRules;

const COLS = {
  name: { xs: 24, sm: 12, md: 5 },
  description: { xs: 24, sm: 12, md: 6 },
  maximumQuantity: { xs: 12, sm: 6, md: 3 },
  minimumQuantity: { xs: 12, sm: 6, md: 4 },
  isArchived: { xs: 12, sm: 6, md: 2 },
  imageActions: { xs: 12, sm: 6, md: 4 },
};

const getQuantityValue = (event) => {
  const parsed = parseInt(event?.target?.value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const ToyRequiredContents = ({
  isView,
  canEditImages,
  form,
  onUploadImages,
  onPreviewImages,
  contentImageLoading,
}) => {
  const imageInputRefs = useRef({});

  return (
    <Form.Item label="Required Contents">
      <Form.List name="requiredContents">
      {(fields, { add, remove }) => (
        <>
          {fields?.map(({ key, name, ...restField }) => (
            <Row key={key} gutter={[8, 0]} className="toy-content-row">
              <Col {...COLS?.name}>
                <Form.Item
                  {...restField}
                  name={[name, 'name']}
                  label="Name"
                  rules={[{ ...required, message: 'Please Enter Name!' }]}
                >
                  <Input allowClear placeholder="Enter Name" maxLength={100} />
                </Form.Item>
              </Col>
              <Col {...COLS?.description}>
                <Form.Item
                  {...restField}
                  name={[name, 'description']}
                  label="Description"
                >
                  <Input
                    allowClear
                    placeholder="Enter Description"
                    maxLength={200}
                  />
                </Form.Item>
              </Col>
              <Col {...COLS?.maximumQuantity}>
                <Form.Item
                  {...restField}
                  name={[name, 'maximumQuantity']}
                  label="Qty"
                  getValueFromEvent={getQuantityValue}
                  rules={[{ required: true, message: 'Please Enter Qty!' }]}
                >
                  <NumberComponent
                    allowClear
                    isFractional={false}
                    placeholder="Enter Qty"
                    type={NUMBER_FIELD_TYPES?.QUANTITY}
                  />
                </Form.Item>
              </Col>
              <Col {...COLS?.minimumQuantity}>
                <Form.Item
                  {...restField}
                  name={[name, 'minimumQuantity']}
                  label="Min Required Qty"
                  dependencies={['requiredContents', name, 'maximumQuantity']}
                  getValueFromEvent={getQuantityValue}
                  rules={[
                    {
                      required: true,
                      message: 'Please Enter Min Required Qty!',
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const maximum = getFieldValue([
                          'requiredContents',
                          name,
                          'maximumQuantity',
                        ]);
                        if (
                          value === undefined ||
                          maximum === undefined ||
                          value <= maximum
                        ) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error('Min Required Qty cannot be more than Qty!'),
                        );
                      },
                    }),
                  ]}
                >
                  <NumberComponent
                    allowClear
                    isFractional={false}
                    placeholder="Enter Min Qty"
                    type={NUMBER_FIELD_TYPES?.QUANTITY}
                  />
                </Form.Item>
              </Col>
              <Col {...COLS?.isArchived}>
                <Form.Item
                  {...restField}
                  name={[name, 'isArchived']}
                  label="Archived"
                  htmlFor=""
                  valuePropName="checked"
                  initialValue={false}
                >
                  <Switch size="small" />
                </Form.Item>
              </Col>
              <Col {...COLS?.imageActions} className="toy-content-image-actions">
                {(() => {
                  const imageKeys =
                    form?.getFieldValue([
                      'requiredContents',
                      name,
                      'imageKeys',
                    ]) || [];
                  const imageUrls =
                    form?.getFieldValue([
                      'requiredContents',
                      name,
                      'imageUrls',
                    ]) || [];
                  const imageCount = Math.max(
                    imageKeys.length,
                    imageUrls.length,
                  );
                  const remainingImageCount = Math.max(0, 4 - imageCount);

                  return (
                    <Form.Item label="Images">
                      <Space align="center">
                        {canEditImages && remainingImageCount > 0 && (
                          <>
                            <input
                              ref={(element) => {
                                imageInputRefs.current[name] = element;
                              }}
                              type="file"
                              accept="image/jpeg,image/png,image/gif"
                              multiple
                              style={{ display: 'none' }}
                              onChange={(event) => {
                                const input = event.currentTarget;
                                const files = Array.from(input?.files || []);
                                onUploadImages(name, files, key);
                                input.value = '';
                              }}
                            />
                            <Tooltip title="Upload images">
                              <Button
                                type="link"
                                aria-label="Upload required content images"
                                icon={<UploadOutlined />}
                                loading={contentImageLoading?.[key]}
                                disabled={isView}
                                onClick={() => imageInputRefs.current[name]?.click()}
                              >
                                Browse
                              </Button>
                            </Tooltip>
                          </>
                        )}
                        <Tooltip title="View images">
                          <Button
                            type="link"
                            aria-label="View required content images"
                            icon={<EyeOutlined />}
                            disabled={!imageUrls.length}
                            onClick={() => onPreviewImages(imageUrls, name)}
                          />
                        </Tooltip>
                        <Popconfirm
                          title="Remove this required content?"
                          okText="Remove"
                          cancelText="Cancel"
                          onConfirm={() => remove(name)}
                          disabled={isView}
                        >
                          <Button
                            type="link"
                            aria-label="Remove required content"
                            disabled={isView}
                            icon={<CloseOutlined />}
                          />
                        </Popconfirm>
                      </Space>
                      {imageCount === 0 && (
                        <Typography.Text
                          type="secondary"
                          className="toy-content-no-file"
                        >
                          No file selected
                        </Typography.Text>
                      )}
                    </Form.Item>
                  );
                })()}
              </Col>
            </Row>
          ))}
          {fields?.length < TOY_CONTENT_LIST_LIMIT && (
            <Form.Item>
              <Button
                type="dashed"
                onClick={() => add({ isArchived: false })}
                block
                icon={<PlusOutlined />}
              >
                Add Required Content
              </Button>
            </Form.Item>
          )}
        </>
      )}
      </Form.List>
    </Form.Item>
  );
};

export default ToyRequiredContents;
